import { create } from 'zustand';
import apiClient from '../lib/api';

/**
 * Software licences — assets.software_licenses + assets.license_assignments
 * via /assets/licenses. Seats used are counted server-side from active
 * assignments. totalSeats = 0 means unlimited. Non-managers receive a masked
 * licence key. Actions throw an Error with the server's message on failure.
 */

export interface LicenseAssignment {
  id: string;
  userId: string | null;
  userName: string | null;
  userEmail: string | null;
  userDepartment: string | null;
  deviceId: string | null;
  deviceCode: string | null;
  deviceName: string | null;
  assignedDate: string;
  unassignedDate: string | null;
  notes: string | null;
}

export interface SoftwareLicense {
  id: string;
  name: string;
  publisher: string | null;
  licenseKey: string | null;
  licenseType: string | null;
  totalSeats: number;
  seatsUsed: number;
  seatsAvailable: number | null;
  purchaseDate: string | null;
  expiryDate: string | null;
  cost: number | null;
  currency: string;
  supplierId: number | null;
  supplierName: string | null;
  notes: string | null;
  assignments?: LicenseAssignment[];
}

export interface LicenseInput {
  name: string;
  publisher?: string;
  licenseKey?: string;
  licenseType?: string;
  totalSeats?: number | string;
  purchaseDate?: string;
  expiryDate?: string;
  cost?: number | string;
  currency?: string;
  supplierId?: number | string | null;
  notes?: string;
}

const mapAssignment = (a: any): LicenseAssignment => ({
  id: a.id,
  userId: a.user_id,
  userName: a.user_name,
  userEmail: a.user_email,
  userDepartment: a.user_department,
  deviceId: a.device_id,
  deviceCode: a.device_code,
  deviceName: a.device_name,
  assignedDate: a.assigned_date,
  unassignedDate: a.unassigned_date,
  notes: a.notes,
});

const mapLicense = (l: any): SoftwareLicense => ({
  id: l.id,
  name: l.software_name,
  publisher: l.publisher,
  licenseKey: l.license_key,
  licenseType: l.license_type,
  totalSeats: Number(l.total_seats) || 0,
  seatsUsed: Number(l.seats_used) || 0,
  seatsAvailable: l.seats_available ?? null,
  purchaseDate: l.purchase_date,
  expiryDate: l.expiry_date,
  cost: l.cost === null || l.cost === undefined ? null : Number(l.cost),
  currency: l.currency || 'INR',
  supplierId: l.supplier_id ?? null,
  supplierName: l.supplier_name,
  notes: l.notes,
  assignments: Array.isArray(l.assignments) ? l.assignments.map(mapAssignment) : undefined,
});

const toPayload = (v: Partial<LicenseInput>) => ({
  software_name: v.name,
  publisher: v.publisher,
  license_key: v.licenseKey,
  license_type: v.licenseType,
  total_seats: v.totalSeats,
  purchase_date: v.purchaseDate,
  expiry_date: v.expiryDate,
  cost: v.cost,
  currency: v.currency,
  supplier_id: v.supplierId,
  notes: v.notes,
});

const dataOf = (res: any) => res.data?.data ?? res.data;
const errorOf = (e: any, fallback: string) => new Error(e?.response?.data?.message || e?.message || fallback);

interface LicenseState {
  licenses: SoftwareLicense[];
  types: string[];
  loaded: boolean;
  error: string | null;

  fetchLicenses: () => Promise<void>;
  fetchTypes: () => Promise<void>;
  fetchLicense: (id: string) => Promise<SoftwareLicense>;
  createLicense: (v: LicenseInput) => Promise<SoftwareLicense>;
  updateLicense: (id: string, v: Partial<LicenseInput>) => Promise<SoftwareLicense>;
  deleteLicense: (id: string) => Promise<void>;
  assignSeat: (id: string, target: { employeeId?: string; deviceId?: string; notes?: string }) => Promise<SoftwareLicense>;
  releaseSeat: (id: string, assignmentId: string) => Promise<SoftwareLicense>;
}

export const useLicenseStore = create<LicenseState>((set) => {
  /** Replace a licence in the list with a fresh copy from the server. */
  const upsert = (lic: SoftwareLicense) =>
    set((s) => ({
      licenses: s.licenses.some((l) => l.id === lic.id)
        ? s.licenses.map((l) => (l.id === lic.id ? { ...lic, assignments: undefined } : l))
        : [...s.licenses, { ...lic, assignments: undefined }],
    }));

  return {
    licenses: [],
    types: [],
    loaded: false,
    error: null,

    fetchLicenses: async () => {
      try {
        const res = await apiClient.get('/assets/licenses');
        set({ licenses: (dataOf(res) || []).map(mapLicense), loaded: true, error: null });
      } catch (e) {
        set({ loaded: true, error: errorOf(e, 'Could not load licences').message });
      }
    },

    fetchTypes: async () => {
      try {
        const res = await apiClient.get('/assets/licenses/types');
        set({ types: dataOf(res) || [] });
      } catch { /* the form falls back to free text */ }
    },

    fetchLicense: async (id) => {
      try {
        const lic = mapLicense(dataOf(await apiClient.get(`/assets/licenses/${id}`)));
        upsert(lic);
        return lic;
      } catch (e) { throw errorOf(e, 'Could not load licence'); }
    },

    createLicense: async (v) => {
      try {
        const lic = mapLicense(dataOf(await apiClient.post('/assets/licenses', toPayload(v))));
        upsert(lic);
        return lic;
      } catch (e) { throw errorOf(e, 'Could not add licence'); }
    },

    updateLicense: async (id, v) => {
      try {
        const lic = mapLicense(dataOf(await apiClient.patch(`/assets/licenses/${id}`, toPayload(v))));
        upsert(lic);
        return lic;
      } catch (e) { throw errorOf(e, 'Could not update licence'); }
    },

    deleteLicense: async (id) => {
      try {
        await apiClient.delete(`/assets/licenses/${id}`);
        set((s) => ({ licenses: s.licenses.filter((l) => l.id !== id) }));
      } catch (e) { throw errorOf(e, 'Could not remove licence'); }
    },

    assignSeat: async (id, target) => {
      try {
        const lic = mapLicense(dataOf(await apiClient.post(`/assets/licenses/${id}/assign`, target)));
        upsert(lic);
        return lic;
      } catch (e) { throw errorOf(e, 'Could not assign seat'); }
    },

    releaseSeat: async (id, assignmentId) => {
      try {
        const lic = mapLicense(dataOf(await apiClient.post(`/assets/licenses/${id}/assignments/${assignmentId}/unassign`)));
        upsert(lic);
        return lic;
      } catch (e) { throw errorOf(e, 'Could not release seat'); }
    },
  };
});
